import { useEffect } from 'react';

import { useDeviceInfo } from '@xing-com/crate-xinglet';
import type { GlobalScope } from '@xing-com/crate-xinglet/internal';
import { useRuntimeConfig } from '@xing-com/crate-xinglet/internal';
import { useAppliedTheme } from '@xing-com/hub';

import { DarkColorscheme } from './dark-colorscheme';
import { ensureUsercentricsV3Loaded } from './ensure-usercentrics-v3-loaded';
import { LightColorscheme } from './light-colorscheme';
import { useUsercentricsEvent } from './use-user-centrics-event';
import { getCookie, setCookie } from './utils';

declare const globalThis: GlobalScope & {
  ucMobileSdk?: unknown;
};

type CookieConsentUsercentricsProps = {
  id?: string;
  disableBanner?: boolean;
  loadInBackground?: boolean;
};

const defaultId = 'Tr_3B1GDkkB1q2';

const useSkipBanner = (): boolean => {
  const { entryPoint } = useRuntimeConfig();
  const isLoginSite = entryPoint === '@xing-com/crate-login-root';

  const { isWebview } = useDeviceInfo();

  const isBrowserEnvironment = typeof window !== 'undefined';
  if (!isBrowserEnvironment) return true;

  const isSyncedFromWebview = typeof globalThis.ucMobileSdk === 'object';
  if (isWebview && !isSyncedFromWebview) return true;

  const host = window.location.host;
  const enabledByCookie = getCookie('crate-testing-allow-consent') === 'true';
  const isLocalhostOrEnv =
    /[k]?env\.xing\.com|localhost:|^192\.168|http:\/\/application$/.test(host);
  if (isLocalhostOrEnv && !enabledByCookie) return true;

  const urlContainsPercy = window.location.href.includes('percy');
  if (urlContainsPercy) return true;

  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  if (isSafari && isLoginSite) return true;

  return false;
};

export const CookieConsentUsercentrics: React.FC<
  CookieConsentUsercentricsProps
> = ({ id = defaultId, disableBanner, loadInBackground = false }) => {
  // set cookie for old consent if not present to prevent it from opening in legacy apps
  useEffect(() => {
    const COOKIE_NAME = 'userConsent';
    const COOKIE_VALUE = JSON.stringify({
      marketing: false,
      version: '5',
    });
    const COOKIE_EXPIRY_DAYS = 365;
    const existingCookie = getCookie(COOKIE_NAME);

    if (!existingCookie) {
      // Set cookie if not present
      setCookie(COOKIE_NAME, COOKIE_VALUE, COOKIE_EXPIRY_DAYS);
    }
  }, []);

  const appliedTheme = useAppliedTheme();
  const isDarkMode = appliedTheme === 'dark';

  const skipBanner = useSkipBanner();
  useUsercentricsEvent();

  useEffect(() => {
    if ((disableBanner && !loadInBackground) || skipBanner) {
      return;
    }

    ensureUsercentricsV3Loaded({
      settingsId: id,
    });
  }, [disableBanner, id, loadInBackground, skipBanner]);

  const ColorschemeComponent = isDarkMode ? DarkColorscheme : LightColorscheme;

  return <ColorschemeComponent />;
};
